import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Users as UsersIcon } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api.config';

import { useModal } from '../../components/layout';
import { User } from "../../types/auth.ts";
import { Card, Form } from 'react-bootstrap';
import NewGroupModal from "../../components/modals/group/NewGroupModal";
import GroupMembersModal from "../../components/modals/group/GroupMembersModal";
import '../../../scss/custom.scss';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

interface Group {
    id: string;
    name: string;
    description: string;
    member_count: number;
    created_at: string;
}

interface GroupFormData {
    name: string;
    description: string;
}

interface PaginationData {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface APIResponse<T> {
    data: T[];
    pagination: PaginationData;
}

const ITEMS_PER_PAGE = 10;

const GroupsDirectory: React.FC = () => {
    const auth = useAuth();
    const [user] = useState<User | null>(auth.user);
    const modalState = useModal();
    const [groups, setGroups] = useState<Group[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [selectedGroupForMembers, setSelectedGroupForMembers] = useState<Group | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const searchTimeout = useRef<NodeJS.Timeout>();
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        limit: ITEMS_PER_PAGE,
        total: 0,
        totalPages: 0
    });
    const [formData, setFormData] = useState<GroupFormData>({
        name: '',
        description: ''
    });

    useEffect(() => {
        fetchGroups(1);
    }, []);

    interface DirectoryUser extends Omit<User, 'groups'> {
        groups: string[];
    }

    const normalizeUser = (user: any): DirectoryUser => ({
        id: user.id,
        email: user.email,
        fullName: user.full_name || user.fullName,
        full_name: user.full_name || user.fullName,
        role: user.role || 'user',
        organizationId: user.organization_id || auth?.user?.organizationId || '',
        organizationDomain: user.organization_domain || auth?.user?.organizationDomain || '', // Add this line
        isSystemOrg: user.is_system_org || auth?.user?.isSystemOrg || false,
        status: user.status || 'active',
        groups: (user.groups || []).map((group: any) =>
            typeof group === 'string' ? group : group.id
        )
    });

    const handleSearchUsers = async (query: string, groupId: string, isMember: boolean): Promise<DirectoryUser[]> => {
        try {
            console.log(`Searching users with isMember=${isMember}, groupId=${groupId}`);

            // Add this debugging log
            const endpoint = isMember
                ? `${API_ENDPOINTS.GROUPS.LIST}/${groupId}/users`
                : `${API_ENDPOINTS.USERS.LIST}?${new URLSearchParams({
                    ...(query ? { q: query } : {}),
                    page: '1',
                    limit: '10'
                })}`;

            console.log(`Calling endpoint: ${endpoint}`);

            const result = await apiService.get<APIResponse<User>>(endpoint);
            console.log(`API response:`, result);

            return (result.data || []).map(normalizeUser);
        } catch (error) {
            console.error('Error searching users:', error);
            modalState.setError(error instanceof Error ? error.message : 'Failed to search users');
            return [];
        }
    };

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        searchTimeout.current = setTimeout(() => {
            setCurrentPage(1);
            fetchGroups(1, value);
        }, 300);
    };

    useEffect(() => {
        const initialize = async () => {
            await fetchGroups(1);
        };
        initialize();
    }, []);

    const fetchGroups = async (page: number, search?: string) => {
        try {
            setIsLoading(true);
            setError(null);

            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ITEMS_PER_PAGE.toString(),
                ...(search ? { search } : {})
            });

            const result = await apiService.get<APIResponse<Group>>(
                `${API_ENDPOINTS.GROUPS.LIST}?${queryParams}`
            );

            setGroups(result.data);
            setPagination(result.pagination);
        } catch (error) {
            console.error('Error fetching groups:', error);
            setError(error instanceof Error ? error.message : 'An error occurred');
            modalState.setError('Failed to fetch groups');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddUserToGroup = async (userId: string, groupId: string) => {
        try {
            await apiService.post(
                `${API_ENDPOINTS.GROUPS.ADD_MEMBER}/${groupId}/users/${userId}`,
                {}
            );
            await fetchGroups(currentPage, searchQuery);
            modalState.setSuccess('User added to group successfully');
        } catch (error) {
            console.error('Error adding user to group:', error);
            modalState.setError('Failed to add user to group');
        }
    };

    const handleRemoveUserFromGroup = async (userId: string, groupId: string) => {
        try {
            await apiService.delete(
                `${API_ENDPOINTS.GROUPS.REMOVE_MEMBER}/${groupId}/users/${userId}`
            );
            await fetchGroups(currentPage, searchQuery);
            modalState.setSuccess('User removed from group successfully');
        } catch (error) {
            console.error('Error removing user from group:', error);
            modalState.setError('Failed to remove user from group');
        }
    };

    const handleCreateGroup = async () => {
        try {
            modalState.setIsLoading(true);
            await apiService.post(API_ENDPOINTS.GROUPS.CREATE, formData);
            await fetchGroups(currentPage, searchQuery);
            setShowModal(false);
            resetForm();
            modalState.setSuccess('Group created successfully');
        } catch (error) {
            console.error('Error creating group:', error);
            modalState.setError('Failed to create group');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleUpdateGroup = async () => {
        if (!selectedGroup) return;

        try {
            modalState.setIsLoading(true);
            await apiService.put(
                `${API_ENDPOINTS.GROUPS.UPDATE}/${selectedGroup.id}`,
                formData
            );
            await fetchGroups(currentPage, searchQuery);
            setShowModal(false);
            resetForm();
            modalState.setSuccess('Group updated successfully');
        } catch (error) {
            console.error('Error updating group:', error);
            modalState.setError('Failed to update group');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleDeleteGroup = async (groupId: string) => {
        if (!window.confirm('Are you sure you want to delete this group?')) return;

        try {
            modalState.setIsLoading(true);
            await apiService.delete(`${API_ENDPOINTS.GROUPS.DELETE}/${groupId}`);
            await fetchGroups(currentPage, searchQuery);
            modalState.setSuccess('Group deleted successfully');
        } catch (error) {
            console.error('Error deleting group:', error);
            modalState.setError('Failed to delete group');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        fetchGroups(newPage, searchQuery);
    };

    const resetForm = () => {
        setFormData({ name: '', description: '' });
        setSelectedGroup(null);
    };

    const renderPagination = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Groups pagination" className="mt-3">
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages}
                    {endPage < pagination.totalPages && (
                        <>
                            {endPage < pagination.totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                >
                                    {pagination.totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (!user) return null;

    return (
        <>
                <div className="page-container">
                    <div className="mb-4">
                        <h5 className="mb-3">Groups</h5>
                        {isLoading ? (
                            <ContentSkeleton />
                        ) : (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                {/* Header Section */}
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div className="flex-grow-1">
                                        <div className="mt-3">
                                            <Form.Group>
                                                <div className="d-flex gap-2">
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Search groups..."
                                                        value={searchQuery}
                                                        onChange={(e) => handleSearch(e.target.value)}
                                                        className="border"
                                                        style={{height: '38px'}}
                                                    />
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => {
                                                            resetForm();
                                                            setShowModal(true);
                                                        }}
                                                        style={{height: '38px', whiteSpace: 'nowrap'}}
                                                    >
                                                        <Plus size={16} className="me-1"/>
                                                        New Group
                                                    </button>
                                                </div>
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Main Content */}
                                <div className="row">
                                    <div className="col-md-8">
                                        {isLoading ? (
                                            <div className="text-center p-2">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : error ? (
                                            <div className="alert alert-danger" role="alert">
                                                {error}
                                            </div>
                                        ) : groups.length === 0 ? (
                                            <div className="text-center py-4 text-muted">
                                                No groups found. Create a new group to get started.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="list-group">
                                                    {groups.map((group) => (
                                                        <div
                                                            key={group.id}
                                                            className={`list-group-item py-2 px-3 ${
                                                                selectedGroup?.id === group.id ? 'selected' : ''
                                                            }`}
                                                            onClick={() => setSelectedGroup(group)}
                                                        >
                                                            <div className="d-flex align-items-start">
                                                                <UsersIcon size={18} className="text-muted me-2 mt-1"/>
                                                                <div className="flex-grow-1">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <h6 className="mb-0 text-truncate">{group.name}</h6>
                                                                            <div className="text-muted small">
                                                                                {group.member_count === 0
                                                                                    ? 'No members'
                                                                                    : group.member_count === 1
                                                                                        ? '1 member'
                                                                                        : `${group.member_count} members`}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {group.description && (
                                                                        <p className="text-muted small mb-0 mt-1">
                                                                            {group.description}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Pagination Info and Controls */}
                                                <div className="d-flex justify-content-between align-items-center mt-3">
                                                    <div className="text-muted small">
                                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                                    </div>
                                                    {renderPagination()}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Action Panel */}
                                    <div className="col-md-4">
                                        <Card className="border shadow-sm">
                                            <Card.Header className="border-bottom px-3 py-2">
                                                <h6 className="mb-0">Actions</h6>
                                            </Card.Header>
                                            <Card.Body className="p-2">
                                                <div className="d-grid gap-1">
                                                    <button
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                        disabled={!selectedGroup}
                                                        onClick={() => {
                                                            if (selectedGroup) {
                                                                setFormData({
                                                                    name: selectedGroup.name,
                                                                    description: selectedGroup.description
                                                                });
                                                                setShowModal(true);
                                                            }
                                                        }}
                                                    >
                                                        <Edit size={16}/>
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                        disabled={!selectedGroup}
                                                        onClick={() => {
                                                            if (selectedGroup) {
                                                                setSelectedGroupForMembers(selectedGroup);
                                                                setShowMembersModal(true);
                                                            }
                                                        }}
                                                    >
                                                        <UsersIcon size={16}/>
                                                        Members
                                                    </button>
                                                    <button
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                        disabled={!selectedGroup}
                                                        onClick={() => selectedGroup && handleDeleteGroup(selectedGroup.id)}
                                                    >
                                                        <Trash2 size={16}/>
                                                        Delete
                                                    </button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                            )}
                    </div>

                    {/* Group Modal */}
                    <NewGroupModal
                        isOpen={showModal}
                        onClose={() => {
                            setShowModal(false);
                            resetForm();
                        }}
                        formData={formData}
                        setFormData={setFormData}
                        selectedGroup={selectedGroup}
                        onSubmit={selectedGroup ? handleUpdateGroup : handleCreateGroup}
                        isLoading={modalState.isLoading}
                    />

                    {/* Group Members Modal */}
                    <GroupMembersModal
                        group={selectedGroupForMembers}
                        isOpen={showMembersModal}
                        onClose={() => {
                            setShowMembersModal(false);
                            setSelectedGroupForMembers(null);
                        }}
                        onAddUser={handleAddUserToGroup}
                        onRemoveUser={handleRemoveUserFromGroup}
                        onSearchUsers={handleSearchUsers}
                    />
                </div>
            </>
    );
};

export default GroupsDirectory;